/**
 * EquipmentThresholdsCard — visão centrada no ativo.
 *
 * Lista todos os thresholds que afetam esse ativo (regras específicas
 * dela + defaults herdados do tenant) e permite override inline.
 *
 * Backend não muda: usa alert_rules + tenant_alert_settings que já existem.
 * Uma "row" pode estar em 3 estados:
 *   - Default: nenhuma rule pra essa combinação kind+role no equipment →
 *              usa o valor herdado do tenant_alert_settings (quando aplicável).
 *   - Override: existe uma rule pra esse equipment com params populados.
 *   - Disabled: existe a rule mas enabled=false (alerta silenciado nessa
 *               ativo — útil pra ativo em manutenção).
 *
 * Permissões: leitura pra todos; edição só pra admin+.
 */

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Battery,
  DoorOpen,
  Edit3,
  Plug,
  PlugZap,
  Gauge,
  Signal,
  Thermometer,
  Trash2,
  WifiOff,
  Zap,
} from "lucide-react";
import clsx from "clsx";
import { toast } from "react-hot-toast";

import { alertRulesApi } from "@/api/alerts";
import {
  qk,
  useAlertRules,
  useTenantAlertSettings,
} from "@/hooks/queries";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorMessage } from "@/api/client";
import { SEVERIDADE_OPCOES } from "@/lib/severidade";
import type {
  AlertRule,
  AlertRuleCreate,
  AlertRuleUpdate,
  RuleKind,
  Severity,
  TenantAlertSettings,
} from "@/types";

import { Card, CardBody, CardHeader } from "./Card";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { Field, Input, Select } from "./Field";
import { Tip } from "./Tip";

// ============================================================================
// Tipos internos
// ============================================================================

/**
 * Identifica um "slot" configurável. Pra temperatura, role distingue
 * interna/produto/externa. Pros demais kinds, role é null (uma rule só).
 */
interface ThresholdSlot {
  kind: RuleKind;
  /** Pra temp_out_of_range: temp_internal | temp_product | temp_external */
  role?: string | null;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const SLOTS: ThresholdSlot[] = [
  {
    kind: "temp_out_of_range",
    role: "temp_internal",
    label: "Temperatura interna",
    icon: <Thermometer className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />,
    description: "Alerta se a leitura sai do range",
  },
  {
    kind: "temp_out_of_range",
    role: "temp_product",
    label: "Temperatura do produto",
    icon: <Thermometer className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
    description: "Alerta se a leitura sai do range",
  },
  {
    kind: "temp_out_of_range",
    role: "temp_external",
    label: "Temperatura externa",
    icon: <Thermometer className="h-4 w-4 text-slate-500 dark:text-slate-400" />,
    description: "Geralmente ambiente — monitorar sem range é comum",
  },
  {
    kind: "door_open_too_long",
    label: "Porta aberta",
    icon: <DoorOpen className="h-4 w-4 text-amber-500" />,
    description: "Alerta se a porta fica aberta acima do limite",
  },
  {
    kind: "device_offline",
    label: "Sensor sem comunicação",
    icon: <WifiOff className="h-4 w-4 text-red-500" />,
    description: "Alerta se algum sensor para de reportar",
  },
  {
    kind: "low_battery",
    label: "Bateria fraca",
    icon: <Battery className="h-4 w-4 text-amber-500" />,
    description: "Avisos por nível baixo de bateria",
  },
  {
    kind: "weak_signal",
    label: "Sinal fraco",
    icon: <Signal className="h-4 w-4 text-slate-500" />,
    description: "Avisos quando RSSI/LQI caem demais",
  },
  {
    kind: "power_off",
    label: "Sem energia",
    icon: <Plug className="h-4 w-4 text-red-500" />,
    description: "Alerta se a tomada deixar de consumir watts",
  },
  {
    kind: "plug_off",
    label: "Tomada desligada",
    icon: <PlugZap className="h-4 w-4 text-red-500" />,
    description: "Alerta se o relé da tomada abrir (proteção desarmada)",
  },
  // Os três abaixo só ganharam formulário na v35. Antes eles existiam no
  // motor e no banco, mas a ÚNICA forma de editá-los era o JSON cru na tela
  // de Gerenciar alertas — o que tornava impossível tirar a edição de lá sem
  // deixá-los órfãos. O `medicao_incoerente` é o mais sensível: a v31 acabou
  // de criá-lo para todo cliente que tem medidor.
  {
    kind: "power_overload",
    label: "Compressor forçando",
    icon: <Gauge className="h-4 w-4 text-orange-500" />,
    description: "Consumo acima da potência nominal, por tempo sustentado",
  },
  {
    kind: "current_spike",
    label: "Pico de corrente",
    icon: <Zap className="h-4 w-4 text-yellow-500" />,
    description: "Corrente muito acima da nominal — curto ou compressor travando",
  },
  {
    kind: "medicao_incoerente",
    label: "Medição congelada",
    icon: <Activity className="h-4 w-4 text-purple-500" />,
    description: "O medidor continua publicando, mas repete o mesmo valor",
  },
];

interface Props {
  equipmentId: string;
  tenantId: string;
}

// ============================================================================
// Helpers — resolver valor "vigente" pra cada slot
// ============================================================================

/** Acha a rule que cobre esse slot pra esse equipment. */
function findRule(rules: AlertRule[], slot: ThresholdSlot): AlertRule | null {
  return (
    rules.find(
      (r) =>
        r.kind === slot.kind &&
        ((slot.role == null && (r.params as any)?.role == null) ||
          (r.params as any)?.role === slot.role),
    ) ?? null
  );
}

/**
 * `tenant_alert_settings` com os params da regra tenant-wide POR CIMA.
 *
 * A tela lia só `tenant_alert_settings` e ignorava regras com
 * `equipment_id IS NULL` — que são exatamente as que governam o ativo
 * quando ela não tem override. Resultado medido em 10/09/26: banco com
 * `offline_after_minutes: 5` na regra do tenant, tela exibindo 15 (o valor
 * do settings). Duas fontes da verdade, e a tela lendo a que não manda.
 *
 * A ordem aqui espelha `_equipments_for_rule` no engine: override do
 * equipamento > regra tenant-wide > tenant_alert_settings. Sobrepor num
 * objeto só faz todos os `defaults?.x` desta tela resolverem certo sem
 * precisar tocar em cada um.
 */
function defaultsEfetivos(
  settings: TenantAlertSettings | undefined,
  regraDoTenant: AlertRule | null,
): TenantAlertSettings | undefined {
  if (!regraDoTenant) return settings;
  const params = (regraDoTenant.params ?? {}) as Record<string, unknown>;
  // Cast necessário: params é JSONB livre e só um subconjunto das chaves
  // coincide com TenantAlertSettings. As que não coincidem são ignoradas
  // pelos call sites, que leem chave a chave.
  return { ...(settings ?? {}), ...params } as TenantAlertSettings;
}

/** Formata o valor "atual" pra exibição na linha. */
function formatThresholdValue(
  slot: ThresholdSlot,
  rule: AlertRule | null,
  defaults: TenantAlertSettings | undefined,
): string {
  const p = (rule?.params ?? {}) as Record<string, any>;
  switch (slot.kind) {
    case "temp_out_of_range": {
      const min = p.setpoint_min;
      const max = p.setpoint_max;
      if (min == null && max == null) return "sem range";
      if (min == null) return `≤ ${max} °C`;
      if (max == null) return `≥ ${min} °C`;
      return `${min} °C  →  ${max} °C`;
    }
    case "door_open_too_long":
      return `${p.door_open_warning_minutes ?? defaults?.door_open_warning_minutes ?? "—"} min`;
    case "device_offline": {
      const ac = p.offline_after_minutes ?? defaults?.offline_after_minutes ?? "—";
      // Espelha o fallback do engine: sem valor de bateria na regra, vale o de AC.
      const bat =
        p.battery_offline_after_minutes ??
        p.offline_after_minutes ??
        defaults?.battery_offline_after_minutes ??
        "—";
      const door = p.offline_after_minutes_by_role?.door;
      if (door != null) return `AC ${ac} min · bateria ${bat} min · porta ${door} min`;
      return ac === bat ? `após ${ac} min` : `AC ${ac} min  ·  bateria ${bat} min`;
    }
    case "low_battery": {
      const warn = p.battery_warning_pct ?? defaults?.battery_warning_pct ?? "—";
      const crit = p.battery_critical_pct ?? defaults?.battery_critical_pct ?? "—";
      return `warning ${warn}%  ·  critical ${crit}%`;
    }
    case "weak_signal": {
      const warn =
        p.signal_warning_dbm ?? defaults?.signal_warning_dbm ?? "—";
      const crit =
        p.signal_critical_dbm ?? defaults?.signal_critical_dbm ?? "—";
      return `warning ${warn} dBm  ·  critical ${crit} dBm`;
    }
    case "power_off": {
      const watts = p.threshold_watts ?? 5;
      const mins = p.off_minutes ?? 10;
      return `< ${watts} W por ${mins} min`;
    }
    case "plug_off":
      return rule ? "relé aberto → alerta imediato" : "—";
    case "power_overload": {
      const pct = p.overload_pct ?? 130;
      const min = p.sustained_minutes ?? 10;
      return `> ${pct}% da nominal por ${min} min`;
    }
    case "current_spike":
      return `> ${p.spike_pct ?? 200}% da corrente nominal`;
    case "medicao_incoerente": {
      const janela = p.window_minutes ?? 15;
      const transicoes = p.max_transicoes_tensao ?? 1;
      return `${janela} min com a tensão mudando no máximo ${transicoes}x`;
    }
    default:
      // Kinds avaliados por site ou por cliente — não têm slot por ativo.
      return "—";
  }
}

// ============================================================================
// Componente principal
// ============================================================================

export function EquipmentThresholdsCard({ equipmentId, tenantId }: Props) {
  const { hasMinRole } = useAuth();
  const canEdit = hasMinRole("admin");

  const rulesQuery = useAlertRules(tenantId);
  const settingsQuery = useTenantAlertSettings(tenantId);
  const qc = useQueryClient();

  // Override desta unidade: é o que o botão edita e o que o save cria.
  const equipmentRules = useMemo(() => {
    return (rulesQuery.data ?? []).filter((r) => r.equipment_id === equipmentId);
  }, [rulesQuery.data, equipmentId]);

  // Regras do tenant (equipment_id IS NULL). Governam o ativo quando ela
  // não tem override — e eram justamente o que esta tela ignorava.
  const tenantRules = useMemo(() => {
    return (rulesQuery.data ?? []).filter((r) => r.equipment_id == null);
  }, [rulesQuery.data]);

  const [editTarget, setEditTarget] = useState<ThresholdSlot | null>(null);

  // O SLO alvo saiu daqui (ver o formulário de cadastro do ativo).
  //
  // Ele é um atributo do ativo — quanto de disponibilidade se espera dele —,
  // não um limite de alerta. Estava neste cabeçalho só porque coube ali, e
  // misturava "o que é este ativo" com "quando avisar sobre ele", que são as
  // duas perguntas que esta tela precisa manter separadas.

  const loading = rulesQuery.isLoading || settingsQuery.isLoading;

  return (
    <>
      <Card className="mt-6">
        <CardHeader
          title="Limites deste ativo"
          subtitle={
            canEdit
              ? "O que vale aqui. Sem ajuste próprio, vale o padrão do cliente."
              : "Limites configurados pra este ativo (somente leitura)."
          }
        />
        <CardBody className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500 dark:text-slate-400">
              Carregando configurações…
            </div>
          ) : (
            <ul className="divide-y divide-surface-100 dark:divide-slate-800">
              {SLOTS.map((slot) => {
                const rule = findRule(equipmentRules, slot);
                const doTenant = findRule(tenantRules, slot);
                return (
                  <ThresholdRow
                    key={`${slot.kind}-${slot.role ?? "default"}`}
                    slot={slot}
                    rule={rule}
                    regraDoTenant={doTenant}
                    defaults={defaultsEfetivos(settingsQuery.data, doTenant)}
                    canEdit={canEdit}
                    onEdit={() => setEditTarget(slot)}
                  />
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {editTarget && (
        <ThresholdEditModal
          slot={editTarget}
          // Opção (a): salvar sempre cria/edita override Deste ativo. A
          // regra do tenant entra só como valor herdado nos placeholders,
          // nunca como alvo de escrita — editar por aqui não pode mudar
          // silenciosamente todos os equipamentos do cliente.
          existingRule={findRule(equipmentRules, editTarget)}
          defaults={defaultsEfetivos(
            settingsQuery.data, findRule(tenantRules, editTarget),
          )}
          equipmentId={equipmentId}
          tenantId={tenantId}
          onClose={() => setEditTarget(null)}
        />
      )}
    </>
  );
}

// ============================================================================
// Linha de cada threshold
// ============================================================================

function ThresholdRow({
  slot,
  rule,
  regraDoTenant,
  defaults,
  canEdit,
  onEdit,
}: {
  slot: ThresholdSlot;
  /** Override deste ativo. Decide o rótulo do botão e o que o save edita. */
  rule: AlertRule | null;
  /** Regra tenant-wide do mesmo slot. Governa quando não há override. */
  regraDoTenant: AlertRule | null;
  defaults: TenantAlertSettings | undefined;
  canEdit: boolean;
  onEdit: () => void;
}) {
  // O que o engine de fato aplica neste ativo. A linha mostra ISTO — antes
  // ela mostrava só o override, e caía no settings quando não havia, exibindo
  // um número que ninguém usava.
  const vigente = rule ?? regraDoTenant;
  const value = formatThresholdValue(slot, vigente, defaults);

  // DOIS estados, não três.
  //
  // A tela dizia `override`, `regra do cliente` e `padrão do sistema` — três
  // palavras para uma pergunta só: de onde vem esse valor. A distinção entre
  // as duas últimas é verdadeira no banco (uma é regra tenant-wide, a outra é
  // default do código) e irrelevante para quem lê: as duas significam "não é
  // específico deste ativo". De onde vem o herdado continua visível na linha
  // de valores logo abaixo, para quem precisar.
  const sourceBadge = (() => {
    if (vigente && !vigente.enabled) {
      return <Badge tone="default">desligado aqui</Badge>;
    }
    if (rule) return <Badge tone="brand">deste ativo</Badge>;
    return <Badge tone="neutral">herdado do cliente</Badge>;
  })();

  return (
    <li className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-surface-50/50 dark:hover:bg-slate-800/30 transition-colors">
      <div className="flex items-start gap-3 min-w-0">
        <div className="flex-none mt-0.5">{slot.icon}</div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {slot.label}
            </span>
            {sourceBadge}
            {vigente && (
              <Badge tone={severityTone(vigente.severity)}>
                {SEVERIDADE_LABEL[vigente.severity] ?? vigente.severity}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            <span className="font-mono text-slate-700 dark:text-slate-300">
              {value}
            </span>
            <span className="ml-2 text-slate-400 dark:text-slate-500">
              · {slot.description}
            </span>
          </div>
        </div>
      </div>
      {canEdit && (
        <Button
          size="sm"
          variant="ghost"
          icon={<Edit3 className="h-3.5 w-3.5" />}
          onClick={onEdit}
        >
          {/* Um verbo só. "Editar" vs "Configurar" codificava se já existia
              ajuste próprio — informação que o badge ao lado já dá, por uma
              regra que o usuário tinha que descobrir sozinho. */}
          Ajustar
        </Button>
      )}
    </li>
  );
}

// A tela inteira está em português; `critical` e `warning` eram as únicas
// palavras em inglês nela, e apareciam em toda linha.
const SEVERIDADE_LABEL: Record<string, string> = {
  critical: "crítico",
  warning:  "atenção",
  info:     "informação",
};

function severityTone(s: Severity): "brand" | "warning" | "danger" {
  if (s === "critical") return "danger";
  if (s === "warning") return "warning";
  return "brand";
}

// ============================================================================
// Modal de edição — form condicional por kind
// ============================================================================

/** Shape do form interno, normalizado pra string (inputs HTML). */
interface FormState {
  severity: Severity;
  enabled: boolean;
  // Override do hysteresis_count — quantos ciclos consecutivos pra disparar.
  // Vazio = usa default (engine decide: 1 pra eventos binários, 3 pra outros).
  hysteresis_count: string;
  // v36 — minutos até o alerta virar `disaster`. Vazio = padrão do kind.
  escalar_apos_minutos: string;
  // temp_out_of_range
  setpoint_min: string;
  setpoint_max: string;
  // door / offline
  door_open_warning_minutes: string;
  offline_after_minutes: string;
  // #173: sensor a bateria dorme e reporta em intervalo bem maior que um
  // Wi-Fi/Tasmota — por isso o threshold é separado. Vazio = herda o valor
  // de AC (o engine faz esse fallback), e só cai no default do tenant se
  // nenhum dos dois for preenchido.
  battery_offline_after_minutes: string;
  // #176: sensor de porta é orientado a evento — só reporta quando abre ou
  // fecha. Medido em campo, chega a 9h de silêncio em operação normal, contra
  // 30min do sensor de temperatura. Um valor único pra "bateria" ou faz a
  // porta alertar à toa ou deixa a temperatura cega. Vazio = usa o de bateria.
  door_offline_after_minutes: string;
  // bateria
  battery_warning_pct: string;
  battery_critical_pct: string;
  // sinal
  signal_warning_dbm: string;
  signal_critical_dbm: string;
  // power
  threshold_watts: string;
  off_minutes: string;
  // plug_off
  stale_after_minutes: string;
  // power_overload
  overload_pct: string;
  sustained_minutes: string;
  // current_spike
  spike_pct: string;
  // medicao_incoerente
  window_minutes: string;
  min_minutos_com_dado: string;
  max_transicoes_tensao: string;
}

/**
 * Form inicial — SOMENTE o que a regra tem gravado.
 *
 * Antes isto pre-preenchia os campos com os defaults do tenant, e o efeito
 * colateral era grave: abrir o modal e salvar materializava o default como
 * override explicito na regra. Dali em diante mudar o valor no cliente nao
 * propagava mais pra ativo, sem nenhum aviso — foi assim que um
 * `setpoint_max` do tenant passou a brigar com o range do ativo.
 *
 * Campo vazio agora significa mesmo "herda", e o valor herdado aparece no
 * placeholder (ver `placeholderHerdado`).
 */
function buildInitialForm(
  slot: ThresholdSlot,
  rule: AlertRule | null,
): FormState {
  const p = (rule?.params ?? {}) as Record<string, any>;
  const s = (v: unknown): string => (v == null ? "" : String(v));
  return {
    severity: rule?.severity ?? (slot.kind === "temp_out_of_range" ? "critical" : "warning"),
    enabled: rule?.enabled ?? true,
    hysteresis_count: s(p.hysteresis_count),
    escalar_apos_minutos: s(p.escalar_apos_minutos),
    setpoint_min: s(p.setpoint_min),
    setpoint_max: s(p.setpoint_max),
    door_open_warning_minutes: s(p.door_open_warning_minutes),
    offline_after_minutes: s(p.offline_after_minutes),
    stale_after_minutes: s(p.stale_after_minutes),
    battery_offline_after_minutes: s(p.battery_offline_after_minutes),
    door_offline_after_minutes: s(p.offline_after_minutes_by_role?.door),
    battery_warning_pct: s(p.battery_warning_pct),
    battery_critical_pct: s(p.battery_critical_pct),
    signal_warning_dbm: s(p.signal_warning_dbm),
    signal_critical_dbm: s(p.signal_critical_dbm),
    threshold_watts: s(p.threshold_watts),
    off_minutes: s(p.off_minutes),
    overload_pct: s(p.overload_pct),
    sustained_minutes: s(p.sustained_minutes),
    spike_pct: s(p.spike_pct),
    window_minutes: s(p.window_minutes),
    min_minutos_com_dado: s(p.min_minutos_com_dado),
    max_transicoes_tensao: s(p.max_transicoes_tensao),
  };
}

/**
 * Placeholder de campo vazio: diz o valor que REALMENTE vale e de onde vem.
 *
 * Isto substitui a maior categoria de texto de ajuda da interface — as
 * frases do tipo "vazio usa 15 min". Aquilo nao era ajuda, era o campo nao
 * sabendo dizer o que estava valendo; escondê-las atras de um icone teria
 * mantido o problema e so tirado ele da vista.
 */
function placeholderHerdado(
  valor: number | string | null | undefined,
  origem: string,
): string {
  if (valor == null || valor === "") return "sem limite";
  return `${valor} (${origem})`;
}

/** Kinds cuja evidência é inequívoca na 1ª leitura — o engine usa histerese 1. */
const EVENTO_BINARIO = new Set<RuleKind>([
  "door_open_too_long",
  "device_offline",
  "power_off",
  "plug_off",
  // #186: o veredito já é uma janela agregada de 15 min — três ciclos em
  // cima disso só atrasariam o aviso sem filtrar ruído nenhum.
  "medicao_incoerente",
]);

/**
 * Espelha `ESCALAR_PADRAO_MIN` do engine (alerts/engine.py).
 *
 * Duplicação consciente e o teste `test_o_front_espelha_os_defaults_de_escalonamento`
 * a mantém honesta: o front precisa mostrar o valor herdado no placeholder,
 * e ir buscar isso na API por um número que muda uma vez por ano seria mais
 * máquina do que o problema pede. Se divergirem, o teste quebra.
 */
const ESCALAR_PADRAO_MIN: Partial<Record<RuleKind, number>> = {
  temp_out_of_range: 10,
  power_off: 15,
  plug_off: 15,
  door_open_too_long: 15,
};

const DO_CLIENTE = "herdado do cliente";
const DO_SISTEMA = "padrão do sistema";

function buildParamsFromForm(slot: ThresholdSlot, f: FormState): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  // Role só pra temperatura — fixo pelo slot.
  if (slot.kind === "temp_out_of_range" && slot.role) {
    params.role = slot.role;
  }
  const num = (v: string): number | undefined => {
    if (v.trim() === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  // Hysteresis é comum a todos os kinds — sempre incluir se preenchido.
  const hyst = num(f.hysteresis_count);
  if (hyst != null && hyst >= 1) params.hysteresis_count = Math.floor(hyst);
  // 0 é valor VÁLIDO aqui, e não "vazio": significa que esta regra nunca
  // escala para Disaster. Por isso o `>= 0` — com `>= 1`, o zero era
  // descartado no caminho e o motor voltava a aplicar o padrão do kind.
  const esc = num(f.escalar_apos_minutos);
  if (esc != null && esc >= 0) params.escalar_apos_minutos = Math.floor(esc);
  switch (slot.kind) {
    case "temp_out_of_range":
      if (num(f.setpoint_min) != null) params.setpoint_min = num(f.setpoint_min);
      if (num(f.setpoint_max) != null) params.setpoint_max = num(f.setpoint_max);
      break;
    case "door_open_too_long":
      if (num(f.door_open_warning_minutes) != null) params.door_open_warning_minutes = num(f.door_open_warning_minutes);
      break;
    case "device_offline":
      if (num(f.offline_after_minutes) != null) params.offline_after_minutes = num(f.offline_after_minutes);
      if (num(f.battery_offline_after_minutes) != null)
        params.battery_offline_after_minutes = num(f.battery_offline_after_minutes);
      // by_role só entra no params quando preenchido — vazio significa
      // "herda o de bateria", e gravar a chave com null confundiria o driver.
      if (num(f.door_offline_after_minutes) != null)
        params.offline_after_minutes_by_role = { door: num(f.door_offline_after_minutes) };
      break;
    case "low_battery":
      if (num(f.battery_warning_pct) != null) params.battery_warning_pct = num(f.battery_warning_pct);
      if (num(f.battery_critical_pct) != null) params.battery_critical_pct = num(f.battery_critical_pct);
      break;
    case "weak_signal":
      if (num(f.signal_warning_dbm) != null) params.signal_warning_dbm = num(f.signal_warning_dbm);
      if (num(f.signal_critical_dbm) != null) params.signal_critical_dbm = num(f.signal_critical_dbm);
      break;
    case "power_off":
      if (num(f.threshold_watts) != null) params.threshold_watts = num(f.threshold_watts);
      if (num(f.off_minutes) != null) params.off_minutes = num(f.off_minutes);
      break;
    case "plug_off":
      if (num(f.stale_after_minutes) != null)
        params.stale_after_minutes = num(f.stale_after_minutes);
      break;
    case "power_overload":
      if (num(f.overload_pct) != null) params.overload_pct = num(f.overload_pct);
      if (num(f.sustained_minutes) != null) params.sustained_minutes = num(f.sustained_minutes);
      break;
    case "current_spike":
      if (num(f.spike_pct) != null) params.spike_pct = num(f.spike_pct);
      break;
    case "medicao_incoerente":
      if (num(f.window_minutes) != null) params.window_minutes = num(f.window_minutes);
      if (num(f.min_minutos_com_dado) != null)
        params.min_minutos_com_dado = num(f.min_minutos_com_dado);
      if (num(f.max_transicoes_tensao) != null)
        params.max_transicoes_tensao = num(f.max_transicoes_tensao);
      break;
  }
  return params;
}

function ThresholdEditModal({
  slot,
  existingRule,
  defaults,
  equipmentId,
  tenantId,
  onClose,
}: {
  slot: ThresholdSlot;
  existingRule: AlertRule | null;
  defaults: TenantAlertSettings | undefined;
  equipmentId: string;
  tenantId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() =>
    buildInitialForm(slot, existingRule),
  );

  const createMut = useMutation({
    mutationFn: (p: AlertRuleCreate) => alertRulesApi.create(p, tenantId),
    onSuccess: () => {
      toast.success("Threshold configurado");
      void qc.invalidateQueries({ queryKey: ["alert-rules"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: AlertRuleUpdate }) =>
      alertRulesApi.update(id, p),
    onSuccess: () => {
      toast.success("Threshold atualizado");
      void qc.invalidateQueries({ queryKey: ["alert-rules"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => alertRulesApi.remove(id),
    onSuccess: () => {
      toast.success("Override removido — voltou ao padrão");
      void qc.invalidateQueries({ queryKey: ["alert-rules"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  function handleSave() {
    const params = buildParamsFromForm(slot, form);
    if (existingRule) {
      updateMut.mutate({
        id: existingRule.id,
        p: { severity: form.severity, params, enabled: form.enabled },
      });
    } else {
      createMut.mutate({
        equipment_id: equipmentId,
        kind: slot.kind,
        severity: form.severity,
        params,
        enabled: form.enabled,
      });
    }
  }

  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={slot.label}
      description="Ajuste só para este ativo — em branco, vale o padrão do cliente."
      size="md"
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <div>
            {existingRule && (
              <Button
                size="sm"
                variant="danger"
                icon={<Trash2 className="h-3.5 w-3.5" />}
                loading={deleteMut.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      "Remover o ajuste deste ativo? Ele volta a usar o padrão do cliente.",
                    )
                  ) {
                    deleteMut.mutate(existingRule.id);
                  }
                }}
              >
                Voltar ao padrão
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={handleSave} loading={isSaving}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <ThresholdFields
          slot={slot}
          form={form}
          setForm={setForm}
          defaults={defaults}
        />

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-surface-100 dark:border-slate-800">
          <Field label="Severidade">
            <Select
              value={form.severity}
              onChange={(e) =>
                setForm((f) => ({ ...f, severity: e.target.value as Severity }))
              }
              options={SEVERIDADE_OPCOES}
            />
          </Field>
          <Field label="Status">
            <Select
              value={form.enabled ? "on" : "off"}
              onChange={(e) =>
                setForm((f) => ({ ...f, enabled: e.target.value === "on" }))
              }
              options={[
                { value: "on", label: "Ativo" },
                { value: "off", label: "Silenciado" },
              ]}
            />
          </Field>
        </div>

        <Field
          label={
            <>
              Confirmar após N avaliações
              <Tip label="O que é confirmar após N avaliações">
                Quantas avaliações seguidas precisam concordar antes do alerta
                subir. Evento binário — porta, relé, sensor mudo — não usa: é
                inequívoco já na primeira.
                <br /><br />
                <strong>Cuidado:</strong> conta avaliações do motor (uma por
                minuto), não leituras novas do sensor. Um sensor que reporta a
                cada 6 minutos tem a mesma leitura avaliada seis vezes — então
                três ciclos pode ser uma leitura só, contada três vezes. Para
                exigir persistência de verdade, use o escalonamento abaixo.
              </Tip>
            </>
          }
        >
          <Input
            type="number"
            min={1}
            max={20}
            value={form.hysteresis_count}
            onChange={(e) =>
              setForm((f) => ({ ...f, hysteresis_count: e.target.value }))
            }
            placeholder={placeholderHerdado(
              EVENTO_BINARIO.has(slot.kind) ? 1 : (defaults?.hysteresis_count ?? 3),
              EVENTO_BINARIO.has(slot.kind) ? DO_SISTEMA : DO_CLIENTE,
            )}
          />
        </Field>

        <Field
          label={
            <>
              Virar Disaster após (min)
              <Tip label="O que muda ao virar Disaster">
                O alerta abre em crítico e, se ninguém resolver dentro desse
                tempo, sobe para <strong>Disaster</strong> — mesma ocorrência,
                um degrau a mais na linha do tempo. É esse degrau que autoriza
                o canal de telefone a ligar.
                <br /><br />
                Em branco usa o padrão do tipo de alerta: câmara fora da faixa
                sobe em 10 min; sem energia, tomada desarmada e porta aberta, em
                15.
                <br /><br />
                <strong>Zero</strong> significa que esta regra nunca sobe para
                Disaster — ela fica na severidade em que abriu, para sempre.
                Use quando o alerta importa mas não é de acordar ninguém.
              </Tip>
            </>
          }
        >
          <Input
            type="number"
            min={0}
            max={1440}
            value={form.escalar_apos_minutos}
            onChange={(e) =>
              setForm((f) => ({ ...f, escalar_apos_minutos: e.target.value }))
            }
            placeholder={placeholderHerdado(
              ESCALAR_PADRAO_MIN[slot.kind] ?? "não escala", DO_SISTEMA,
            )}
          />
        </Field>
      </div>
    </Modal>
  );
}

// ============================================================================
// Campos específicos por kind
// ============================================================================

function ThresholdFields({
  slot,
  form,
  setForm,
  defaults,
}: {
  slot: ThresholdSlot;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  defaults: TenantAlertSettings | undefined;
}) {
  const set =
    <K extends keyof FormState>(key: K) =>
    (v: string) =>
      setForm((f) => ({ ...f, [key]: v }));

  switch (slot.kind) {
    case "temp_out_of_range":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mínimo (°C)">
            <Input
              type="number"
              step="0.1"
              value={form.setpoint_min}
              onChange={(e) => set("setpoint_min")(e.target.value)}
              placeholder="sem limite inferior"
            />
          </Field>
          <Field label="Máximo (°C)">
            <Input
              type="number"
              step="0.1"
              value={form.setpoint_max}
              onChange={(e) => set("setpoint_max")(e.target.value)}
              placeholder="sem limite superior"
            />
          </Field>
        </div>
      );
    case "door_open_too_long":
      return (
        <Field
          label={
            <>
              Alerta após (minutos)
              <Tip label="Como conta o tempo de porta aberta">
                Tempo <strong>contínuo</strong> de porta aberta. Fechar e abrir
                de novo reinicia a contagem.
              </Tip>
            </>
          }
        >
          <Input
            type="number"
            min={1}
            value={form.door_open_warning_minutes}
            onChange={(e) => set("door_open_warning_minutes")(e.target.value)}
            placeholder={placeholderHerdado(
              defaults?.door_open_warning_minutes, DO_CLIENTE,
            )}
          />
        </Field>
      );
    case "device_offline":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={
              <>
                Sensor AC (min)
                <Tip label="Como o sensor AC reporta">
                  Tomada e probe Wi-Fi têm energia de sobra e reportam a cada
                  poucos segundos. Medido em campo: mediana de 6s, pior intervalo
                  de 6 min.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min={1}
              value={form.offline_after_minutes}
              onChange={(e) => set("offline_after_minutes")(e.target.value)}
              placeholder={placeholderHerdado(
                defaults?.offline_after_minutes, DO_CLIENTE,
              )}
            />
          </Field>
          <Field
            label={
              <>
                Sensor a bateria (min)
                <Tip label="Por que bateria tem limite próprio">
                  Sensor Zigbee dorme entre leituras pra durar meses — reporta a
                  cada 30-60 min. Cobrar dele o mesmo silêncio de um sensor AC
                  gera alarme falso todo dia.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min={1}
              value={form.battery_offline_after_minutes}
              onChange={(e) => set("battery_offline_after_minutes")(e.target.value)}
              placeholder={placeholderHerdado(
                form.offline_after_minutes.trim() !== ""
                  ? form.offline_after_minutes
                  : defaults?.battery_offline_after_minutes,
                form.offline_after_minutes.trim() !== ""
                  ? "herda o de AC"
                  : DO_CLIENTE,
              )}
            />
          </Field>
          <Field
            label={
              <>
                Sensor de porta (min)
                <Tip label="Por que a porta tem limite próprio">
                  A porta só publica quando abre ou fecha. Em operação normal
                  ela fica horas em silêncio — medimos 9h — e isso não é falha.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min={1}
              value={form.door_offline_after_minutes}
              onChange={(e) => set("door_offline_after_minutes")(e.target.value)}
              placeholder={placeholderHerdado(
                form.battery_offline_after_minutes.trim() !== ""
                  ? form.battery_offline_after_minutes
                  : form.offline_after_minutes.trim() !== ""
                    ? form.offline_after_minutes
                    : defaults?.battery_offline_after_minutes,
                "herda o de bateria",
              )}
            />
          </Field>
        </div>
      );
    case "plug_off":
      return (
        <Field
          label={
            <>
              Idade máxima da leitura (min)
              <Tip label="O que é idade máxima da leitura">
                Não é tempo de espera: o alerta dispara na primeira avaliação
                que vê o relé aberto. Isto define até que idade uma leitura
                ainda vale como retrato do agora. Janela curta não protege de
                nada — só aumenta a chance de não haver dado fresco e a regra
                ficar sem base pra avaliar.
              </Tip>
            </>
          }
        >
          <Input
            type="number"
            min={1}
            value={form.stale_after_minutes}
            onChange={(e) => set("stale_after_minutes")(e.target.value)}
            placeholder={placeholderHerdado(10, DO_SISTEMA)}
          />
        </Field>
      );
    case "low_battery":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Warning (%)">
            <Input
              type="number"
              min={0}
              max={100}
              value={form.battery_warning_pct}
              onChange={(e) => set("battery_warning_pct")(e.target.value)}
              placeholder={placeholderHerdado(
                defaults?.battery_warning_pct, DO_CLIENTE,
              )}
            />
          </Field>
          <Field label="Critical (%)">
            <Input
              type="number"
              min={0}
              max={100}
              value={form.battery_critical_pct}
              onChange={(e) => set("battery_critical_pct")(e.target.value)}
              placeholder={placeholderHerdado(
                defaults?.battery_critical_pct, DO_CLIENTE,
              )}
            />
          </Field>
        </div>
      );
    case "weak_signal":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={
              <>
                Warning (dBm)
                <Tip label="Como ler a escala dBm">
                  Escala negativa: -60 é sinal bom, -90 é ruim. Quanto mais
                  negativo, pior.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              value={form.signal_warning_dbm}
              onChange={(e) => set("signal_warning_dbm")(e.target.value)}
              placeholder={placeholderHerdado(
                defaults?.signal_warning_dbm, DO_CLIENTE,
              )}
            />
          </Field>
          <Field label="Critical (dBm)">
            <Input
              type="number"
              value={form.signal_critical_dbm}
              onChange={(e) => set("signal_critical_dbm")(e.target.value)}
              placeholder={placeholderHerdado(
                defaults?.signal_critical_dbm, DO_CLIENTE,
              )}
            />
          </Field>
        </div>
      );
    case "power_off":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={
              <>
                Limiar (W)
                <Tip label="O que o limiar em watts significa">
                  Abaixo desta potência o compressor é considerado parado.
                  Não confunde com a tomada desarmada: quem lê o relé é a regra
                  <em> Tomada desligada</em>.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min={0}
              step="0.1"
              value={form.threshold_watts}
              onChange={(e) => set("threshold_watts")(e.target.value)}
              placeholder={placeholderHerdado(5, DO_SISTEMA)}
            />
          </Field>
          <Field
            label={
              <>
                Por quanto tempo (min)
                <Tip label="Como escolher esse tempo">
                  Precisa ficar acima do descanso normal do compressor — que é
                  longo e se resolve sozinho. Abaixo disso o alerta vira ruído
                  diário. Na Geladeira Interna o pior ciclo normal medido foi de
                  53 min.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min={1}
              value={form.off_minutes}
              onChange={(e) => set("off_minutes")(e.target.value)}
              placeholder={placeholderHerdado(10, DO_SISTEMA)}
            />
          </Field>
        </div>
      );
    case "power_overload":
      return (
        <div className="space-y-3">
          {/* A regra depende da potência nominal cadastrada. Sem ela o motor
              devolve "regra inativa" e não dispara nunca — em silêncio. Quem
              abre este formulário precisa saber disso ANTES de configurar um
              limite que não vai valer. */}
          <p className="rounded-md bg-amber-50 p-2.5 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            Depende da <strong>potência nominal</strong> do ativo, no cadastro.
            Sem ela esta regra fica inerte — existe, mas não dispara.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={
                <>
                  Acima de (% da nominal)
                  <Tip label="Por que 130% é o padrão">
                    Compressor puxa mais que a nominal em condição normal de
                    verão. 130% sustentado é o ponto em que deixa de ser
                    variação e passa a indicar vedação ruim, condensador sujo
                    ou defeito a caminho.
                  </Tip>
                </>
              }
            >
              <Input
                type="number"
                min={100}
                step="1"
                value={form.overload_pct}
                onChange={(e) => set("overload_pct")(e.target.value)}
                placeholder={placeholderHerdado(130, DO_SISTEMA)}
              />
            </Field>
            <Field
              label={
                <>
                  Sustentado por (min)
                  <Tip label="Por que exigir tempo">
                    Sobrecarga instantânea é partida de compressor, e acontece
                    todo dia. O que interessa é a que não passa.
                  </Tip>
                </>
              }
            >
              <Input
                type="number"
                min={1}
                value={form.sustained_minutes}
                onChange={(e) => set("sustained_minutes")(e.target.value)}
                placeholder={placeholderHerdado(10, DO_SISTEMA)}
              />
            </Field>
          </div>
        </div>
      );
    case "current_spike":
      return (
        <div className="space-y-3">
          <p className="rounded-md bg-amber-50 p-2.5 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            Depende da <strong>potência e da tensão nominais</strong> do ativo,
            no cadastro — é delas que sai a corrente esperada. Sem as duas, a
            regra fica inerte.
          </p>
          <Field
            label={
              <>
                Acima de (% da corrente nominal)
                <Tip label="Por que 200%, e o que o throttle muda">
                  A partida do compressor chega a 5–7× a nominal por um ou dois
                  segundos — por isso o limite é alto e a histerese existe.
                  Importante: em medidor com <em>throttle</em> configurado no
                  gateway, o transiente pode simplesmente não ser publicado, e
                  aí esta regra não vê o pico. Ela não substitui a proteção
                  elétrica do quadro.
                </Tip>
              </>
            }
          >
            <Input
              type="number"
              min={100}
              step="1"
              value={form.spike_pct}
              onChange={(e) => set("spike_pct")(e.target.value)}
              placeholder={placeholderHerdado(200, DO_SISTEMA)}
            />
          </Field>
        </div>
      );
    case "medicao_incoerente":
      return (
        <div className="space-y-3">
          <p className="rounded-md bg-surface-50 p-2.5 text-xs text-slate-600 dark:bg-slate-900/50 dark:text-slate-400">
            Medidor que trava repetindo o último valor continua parecendo
            saudável: o painel fica verde e as outras regras de energia passam
            a confiar num número congelado. É o par do keepalive — sem este
            aviso, um keepalive que parou de bater mente em silêncio.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <Field
              label={
                <>
                  Janela (min)
                  <Tip label="O que a janela observa">
                    Por quanto tempo a tensão precisa ficar sem variar para
                    contar como congelada.
                  </Tip>
                </>
              }
            >
              <Input
                type="number"
                min={5}
                value={form.window_minutes}
                onChange={(e) => set("window_minutes")(e.target.value)}
                placeholder={placeholderHerdado(15, DO_SISTEMA)}
              />
            </Field>
            <Field
              label={
                <>
                  Mínimo com dado (min)
                  <Tip label="Por que exigir um piso">
                    Medidor que quase não reportou na janela não prova nada —
                    poucos pontos iguais podem ser coincidência, não travamento.
                    Abaixo deste piso a regra se abstém.
                  </Tip>
                </>
              }
            >
              <Input
                type="number"
                min={1}
                value={form.min_minutos_com_dado}
                onChange={(e) => set("min_minutos_com_dado")(e.target.value)}
                placeholder={placeholderHerdado(8, DO_SISTEMA)}
              />
            </Field>
            <Field
              label={
                <>
                  Mudanças de tensão
                  <Tip label="Quantas vezes o valor pode mudar">
                    Conta quantas VEZES a tensão mudou na janela, não quantos
                    valores diferentes apareceram — um medidor que reporta
                    volts inteiros mostra dois ou três valores mesmo medindo
                    perfeitamente, e o critério antigo o acusava de congelado.
                    <br /><br />
                    Acima deste teto, a medição está viva. Não é zero porque o
                    medidor congelado emite uma atualização solta de vez em
                    quando (medido: uma a cada 84 min), e com zero o alerta
                    ficaria piscando.
                  </Tip>
                </>
              }
            >
              <Input
                type="number"
                min={0}
                value={form.max_transicoes_tensao}
                onChange={(e) => set("max_transicoes_tensao")(e.target.value)}
                placeholder={placeholderHerdado(1, DO_SISTEMA)}
              />
            </Field>
          </div>
        </div>
      );
  }
}
